import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
  forwardRef,
  input,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, finalize } from 'rxjs';

import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { DSS_HTTP_SERVICE } from '../httpService';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { apiResponse } from 'app/Models/apiResponse';

@Component({
  selector: 'app-ngselect',
  imports: [FormsModule, CommonModule, NgSelectModule, NgbModule],

  templateUrl: './ngselect.component.html',
  styleUrls: ['./ngselect.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    NgSelectModule,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgCustomSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgCustomSelectComponent implements ControlValueAccessor {
  @ViewChild('selectRef') selectRef!: NgSelectComponent;
  @Input() addNewText: boolean = false;
  @Input() ngModel: any;
  @Input() displayLabel: string[];
  @Output() ngModelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() change = new EventEmitter();

  @Input() bindLabel!: string;
  @Input() bindValue!: string;
  @Input() bindSubObj = [];
  @Input() name!: string;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() apiParams: any = {};
  @Input() multiple: boolean = false;
  @Input() nullableProperty: any;
  @Input() defaultProperty: any;
  @Input() comboPosition: 'auto' | 'top' | 'bottom' = 'auto';
  @Input('applyMargins') applyMargins: boolean = true;
  @Input('label') label: string = '';
  @Input() addTagFn!: (name?: string) => any;

  @Input() url!: string;
  @Output() onSelect = new EventEmitter<any>();

  searchTextChanged = new Subject<string>();
  items: any[] = [];
  objectsArray = [];
  pageSize: number = 10;
  currentPage: number = 1;
  searchValue: string = '';
  modelChanged: Subject<string> = new Subject<string>();
  loading: boolean = false;
  private _value: any = '';
  private onChangeCallback: (value: any) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  constructor(
    private modalService: NgbModal,

    @Optional() @Inject(DSS_HTTP_SERVICE) private httpService: any,
    private cd: ChangeDetectorRef
  ) {
    this.modelChanged
      .pipe(debounceTime(1000), distinctUntilChanged()) // debounce search input
      .subscribe((model) => this.SearchTextChanged(model));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultProperty']?.currentValue) {
      if (Object.keys(this.defaultProperty).length > 0) {
        this.items = [this.defaultProperty];
        this.writeValue(this.defaultProperty[this.bindValue]);
        this.change.emit(this.defaultProperty);
      } else if (changes['apiParams']?.currentValue) {
        this.loadMore();
      }
    }
  }

  // openAddModal() {
  //   if (!this.AddComponent) return;

  //   const modalRef = this.modalService.open(this.AddComponent, {
  //     backdrop: 'static',
  //     keyboard: false,
  //     animation: false,
  //     centered: true, // vertical center

  //   });

  //   modalRef.componentInstance.precityName = this.searchValue;

  //   // handle modal close
  //   modalRef.result
  //     .then((newItem) => {
  //       if (newItem) {
  //         // add to ng-select items
  //         this.items.push(newItem);
  //         this.writeValue(newItem[this.bindValue]);

  //       }
  //     })
  //     .catch(() => { });
  // }

  defaultAddTag() {
    // const newItem = { [this.bindValue]: this.items.length + 1, [this.bindLabel]: this.searchValue };
    // this.items.push(newItem);
    // // Set the new value as selected
    // return newItem;
    this.addTagFn(this.searchValue);
  }

  // addTagHandler() {
  //   return this.addTagFn ?? this.defaultAddTag;
  // }

  writeValue(value: any): void {
    this._value = value;
    this.ngModel = value;
    this.ngModelChange.emit(this.ngModel);
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  OnSearch(text: any): void {
    this.modelChanged.next(text);
  }

  SearchTextChanged(e: any): void {
    this.searchValue = e.term;

    const params = [{ key: this.bindLabel, value: e.term }];
    if (this.apiParams) this.apiParams.orelse = true;
    this.loading = true;
    this.httpService
      .post(this.url, { pageNumber: 1, keys: params }, this.apiParams)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (data: apiResponse) => {
          this.items = data.data;
          if (this.nullableProperty) this.items.unshift(this.nullableProperty);
        },
        error: (err) => {
          alert(err.message);
        },
      });
  }

  onOpen() {
    if (this.items.length === 0) this.loadMore();
  }
  loadMore(): void {
    const pageNumber = Math.floor(this.items.length / this.pageSize) + 1;
    if (pageNumber === 1) {
      this.items = [];
    }
    this.loading = true;
    if (this.apiParams) this.apiParams.orelse = true;
    this.httpService
      .post(this.url, { pageNumber }, this.apiParams)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (data: apiResponse) => {
          this.items = [...this.items, ...data.data];

          const isAll = this.items.filter((s) => s[this.bindValue] === '');

          if (this.nullableProperty && isAll.length === 0) {
            this.items.unshift(this.nullableProperty);
          }

          if (this.defaultProperty && this.items.length > 0) {
            const x = this.items.find(
              (s) => s[this.bindValue] === this.defaultProperty[this.bindValue]
            );

            if (x) {
              this.writeValue(x[this.bindValue]);
            }

            this.change.emit(this.defaultProperty);
          }
        },
        error: (err) => {
          alert(err.message);
        },
      });
  }

  subRowObj(item: any, value: any) {
    if (value) {
      const iTms = value.split('.');
      const retVal = iTms.reduce((obj: any, key: any) => obj?.[key], item);
      return retVal;
    }
    return null;
  }

  ShowSeperator(curItem: any, currindex: number) {
    if (this.subRowObj(curItem, this.bindSubObj[currindex]?.value)) {
      let l = this.bindSubObj.length;
      if (currindex < l) return '|';
    }
    return '';
  }

  onChange(event: any) {
    this.ngModel = event[this.bindValue]; // Update the model
    this.ngModelChange.emit(this.ngModel); // Emit the updated model
    this.cd.detectChanges();
    this.change.emit(event); // Notify about the change
  }

  getCombinedLabel(item: any): string {
    let combinedLabel = item[this.bindLabel]; // Start with the main bindLabel

    // Iterate over bindSubObj to build the additional values

    if (this.bindSubObj.length > 0) {
      if (this.displayLabel)
        this.bindSubObj.forEach((sub) => {
          //@ts-ignore
          if (this.displayLabel.includes(sub.key)) {
            const subValue = this.subRowObj(item, sub.value); // Get the nested value
            combinedLabel += subValue ? `- ${subValue}` : ''; // Append the value to the label
          }
        });
      else {
        const sub = this.bindSubObj[0];
        const subValue = this.subRowObj(item, sub.value); // Get the nested value
        combinedLabel += subValue ? `- ${subValue}` : '';
      }
    }
    return combinedLabel;
  }

  OnBlur(): void {
    // this.ngModelChange.emit(this.ngModel); // Emit the updated model
    // this.cd.detectChanges();
    this.onSelect.emit((this.selectRef.selectedItems.map((s) => s.value) as any[])[0]);
  }

  onAddTag(event: any) {
    const newTag = event;
    // Close the dropdown first
    if (this.selectRef) this.selectRef.close();
    // Call parent function
    if (this.addTagFn) {
      this.addTagFn(newTag);
    }
  }
}
